import { withErrorBoundary } from "@sentry/nextjs";
import { useKeenSlider } from "keen-slider/react";
import { useRouter } from "next/router";
import { CSSProperties, FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { useWindowSize } from "hooks";
import { TickIconCircle } from "icons/TIckIconCircle";
import { EVENT_NAMES, trackEvent } from "lib/tracking/segment";
import { theme } from "styles";
import { Pricing5Content } from "types/graphql/generated";
import { Pricing5CardHeader } from "./Pricing5CardHeader";
import { Pricing5CardPrice } from "./Pricing5CardPrice";

type Pricing5MiddleContent = Pick<Pricing5Content, "pricing5Cards">;

interface Props {
  content: Pricing5MiddleContent;
}

const Pricing5MiddleSection: FC<Props> = withErrorBoundary(({ content: { pricing5Cards } }) => {
  const [maxCardPricingHeight, setMaxCardPricingHeight] = useState(0);
  const [maxCardPricegHeight, setMaxCardPriceHeight] = useState(0);
  const fullCarousel = ~~(pricing5Cards?.length > 2);

  const { width } = useWindowSize();
  const { asPath } = useRouter();

  const [ref, instanceRef] = useKeenSlider({
    slides: {
      origin: fullCarousel ? "center" : "auto",
      perView: "auto",
      spacing: 16,
    },
    breakpoints: {
      [`(min-width: ${theme.breakpoints.md + 1}px)`]: {
        disabled: true,
      },
    },
    initial: fullCarousel,
  });

  useEffect(() => {
    if (!instanceRef.current) return;

    const maxCardPricingHeight = Math.max(
      ...Array.from(instanceRef.current!.container.querySelectorAll(".Pricing5__cardPricing")).map(
        (card) => card.clientHeight
      )
    );

    const maxCardPriceHeight = Math.max(
      ...Array.from(instanceRef.current!.container.querySelectorAll(".Pricing5CardPrice")).map(
        (card) => card.clientHeight
      )
    );

    setMaxCardPricingHeight(maxCardPricingHeight);
    setMaxCardPriceHeight(maxCardPriceHeight);
  }, [width, instanceRef]);

  const onClickCta = useCallback(
    ({ label, tier }: { label: string; tier: string }) => {
      // only track on pricing page for RND401K-7420
      if (asPath !== "/pricing") {
        return null;
      }

      const eventName = EVENT_NAMES.buttonClicked as keyof any;

      const data = {
        details: {
          [asPath]: tier.toLowerCase(),
        },
        text: label,
      };

      trackEvent(eventName, data);
    },
    [asPath]
  );

  if (!pricing5Cards.length) {
    return null;
  }

  return (
    <StyledPricing5MiddleSection
      style={
        {
          "--card-pricing-height": `${maxCardPricingHeight}px`,
          "--card-price-height": `${maxCardPricegHeight}px`,
        } as CSSProperties
      }
    >
      <ul className={`Pricing5__cards keen-slider ${!fullCarousel ? "Pricing5__smallCarousel" : ""}`} ref={ref}>
        {pricing5Cards.map((pricingCard) => {
          if (!pricingCard) {
            return null;
          }

          const {
            pricing5Title: { tag, monthlyPrice, discountedMonthlyPrice, participantFee, cardTitleBackground },
            pricing5FeatureTag = { featureTagColor: "" },
            pricing5Cta,
            cardDescription,
            pricing5CardList,
          } = pricingCard;

          const cardCtaColor = pricing5Cta?.cardCtaColor ?? "#fff";
          const cardCtaBackground = pricing5Cta?.cardCtaBackground ?? "#191817";
          const cardCtaLabel = pricing5Cta?.cardCtaLabel;
          const cardCtaLink = pricing5Cta?.cardCtaLink;
          const tabletMinWidth = fullCarousel ? "375px" : "400px";

          return (
            <li
              key={monthlyPrice}
              className="Pricing5__card keen-slider__slide"
              style={
                {
                  "--card-cta-color": cardCtaColor,
                  "--card-cta-background": cardCtaBackground,
                  "--tabletMinWidth": tabletMinWidth,
                } as CSSProperties
              }
            >
              {tag && (
                <Pricing5CardHeader {...pricing5FeatureTag} tag={tag} cardTitleBackground={cardTitleBackground} />
              )}
              <div className="Pricing5__innerContent">
                <div className="Pricing5__topContent">
                  <div className="Pricing5__cardPricingWrapper">
                    <div className="Pricing5__cardPricing">
                      <div className="Pricing5__cardPrice">
                        <Pricing5CardPrice
                          content={{
                            monthlyPrice,
                            discountedMonthlyPrice,
                            participantFee,
                            discountedMonthlyPriceColor: pricing5FeatureTag?.featureTagColor,
                          }}
                        />
                      </div>

                      <Markdown className="Pricing5__cardDescription Font_16_22_450 Color_secondary">
                        {cardDescription}
                      </Markdown>
                    </div>
                  </div>
                  {!!pricing5Cta && cardCtaLabel && cardCtaLink && (
                    <PipelineRoutingLinkButton
                      className="Pricing5__cardCta"
                      compact
                      onClick={() => onClickCta({ label: cardCtaLabel, tier: tag })}
                      href={cardCtaLink}
                    >
                      {cardCtaLabel}
                    </PipelineRoutingLinkButton>
                  )}
                </div>

                <ul className="Pricing5__cardList">
                  {pricing5CardList?.map(
                    ({ cardListDescription = null }) =>
                      cardListDescription && (
                        <li key={cardListDescription} className="Pricing5__cardListItem ]">
                          <TickIconCircle className="Pricing5__tickIcon ]" />
                          <Markdown className="Pricing5__cardListItemDescription ] Font_16_22_450 ]">
                            {cardListDescription}
                          </Markdown>
                        </li>
                      )
                  )}
                </ul>
              </div>
            </li>
          );
        })}
      </ul>
    </StyledPricing5MiddleSection>
  );
}, {});

const StyledPricing5MiddleSection = styled.div`
  margin-top: 64px;

  .Pricing5__cards {
    display: flex;
    padding: 0 32px;
  }

  .Pricing5__cards > * {
    flex: 1;
  }

  .Pricing5__card {
    background: ${({ theme }) => theme.colors.fog["00"]};
    border-radius: 8px;
    max-width: 389.33px;
  }

  .Pricing5__card:not(:first-child) {
    margin-left: 24px;
  }

  .Pricing5__innerContent {
    padding: 32px 24px;
  }

  .Pricing5__topContent {
    padding-bottom: 32px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["20"]};
  }

  .Pricing5__cardPricingWrapper {
    min-height: var(--card-pricing-height);
  }

  .Pricing5__cardPrice {
    min-height: var(--card-price-height);
  }

  .Pricing5__cardDescription {
    margin-top: 24px;
  }

  .Pricing5__cardCta {
    width: fit-content;
    margin-top: 24px;
    color: var(--card-cta-color);
    background-color: var(--card-cta-background);

    &:hover {
      background-color: ${({ theme }) => theme.colors.fog["60"]};
    }

    &:focus {
      background-color: ${({ theme }) => theme.colors.fog["60"]};
    }
  }

  .Pricing5__cardList {
    margin-top: 32px;
  }

  .Pricing5__cardListItem {
    display: flex;
  }

  .Pricing5__cardListItem:not(:first-child) {
    margin-top: 16px;
  }

  .Pricing5__tickIcon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 3px;
  }

  .Pricing5__cardListItemDescription {
    margin-left: 12px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Pricing5__card:not(:first-child) {
      margin-left: 16px;
    }

    .Pricing5__innerContent {
      padding: 32px 16px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    margin-top: 56px;

    .Pricing5__cards {
      padding: 0;
    }

    .Pricing5__card {
      min-width: 375px;
      min-width: --tabletminwidth;
    }

    .Pricing5__card:not(:first-child) {
      margin-left: unset;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Pricing5__smallCarousel {
      margin-left: 16px;
      padding-right: 16px;
    }

    .Pricing5__card {
      min-width: 300px;
    }
  }
`;

export { Pricing5MiddleSection };
